import { HiddenStateSync } from '../../core/selection/HiddenStateSync.js';
import { TABLE_WIDGET_KEY, TABLE_CSS_KEY } from './ITableWidget.js';
import { Widget } from '../../core/Widget.js';

/** 2026-08-16. Класс активной ячейки cell-click */
const CELL_SELECTED_CLASS = '_cellsel';
/** 2026-08-16. Класс строки с активной ячейкой: гасит подсветку строки */
const CELL_ROW_CLASS = '_cellrow';
const INTERACTIVE_CELL_SELECTOR = [
    'a',
    'button',
    'input',
    'select',
    'textarea',
    'label',
    '[contenteditable]',
    "[role='button']",
    '[data-imcmd]'
].join(',');
/**
 * Адаптер выбора/подсветки строк таблицы.
 * Управляет SelectionModel, синхронизирует hidden-поля и классы (_ckrow/_currow),
 * обрабатывает ввод с мыши/клавиатуры.
 */
class TableSelectionAdapter {
    constructor(model, opts) {
        this.active = false; // «текущая» таблица (фокус области)
        this.selectionReason = 'row';
        this.model = model;
        this.root = opts.root;
        this.sync = opts.sync ?? new HiddenStateSync(opts.root, opts.tableKey, 150);
        this.o = {
            tbodySelector: opts.tbodySelector ?? 'tbody',
            rowSelector: opts.rowSelector ?? 'tr:not(.h)',
            idAttr: opts.idAttr ?? 'data-id',
            selectedClass: opts.selectedClass ?? '_ckrow',
            currentClass: opts.currentClass ?? '_currow',
            disabledRowSelector: opts.disabledRowSelector ?? 'tr.ar',
            isEnabled: opts.isEnabled,
            onSelectionChange: opts.onSelectionChange,
            onCellClick: opts.onCellClick,
            isCellClickPending: opts.isCellClickPending
        };
        // Установка текущей строки, если с бека приходит уже подсвеченная строка
        const currow = this.root.querySelector(`.${this.o.currentClass}`);
        const ckrow = this.root.querySelectorAll(`.${this.o.selectedClass}`);
        if (ckrow.length > 0) {
            const ids = [];
            ckrow.forEach((row) => {
                const rowId = this.rowId(row);
                if (rowId)
                    ids.push(rowId);
            });
            this.model.set(ids);
            if (currow)
                this.model.setFocused(this.rowId(currow));
            Widget.closestOnByName(this.root, TABLE_WIDGET_KEY)?.refreshState();
        }
        // подписка на изменение модели — обновляем DOM и hidden-поля
        this.model.onChange(({ selected, focused, diff }) => {
            const tbody = this.tbody();
            if (!tbody)
                return;
            // снять классы у removed
            diff.removed.forEach(id => {
                const tr = this.rowById(id);
                if (tr)
                    tr.classList.remove(this.o.selectedClass);
            });
            // добавить классы у added
            diff.added.forEach(id => {
                const tr = this.rowById(id);
                if (tr)
                    tr.classList.add(this.o.selectedClass);
            });
            // текущая строка (focused) → currentClass
            this.clearCurrent();
            if (focused) {
                const tr = this.rowById(focused);
                if (tr)
                    tr.classList.add(this.o.currentClass);
            }
            // 2026-08-16. Любой не-cell переход selection (клавиатура, selectAll,
            // программный set) снимает подсветку активной ячейки
            if (this.selectionReason !== 'cell')
                this.markActiveCell(null);
            const ids = Array.from(selected);
            this.sync.write(ids, focused ?? null, this.selectionReason === 'cell');
            this.o.onSelectionChange?.(ids, focused ?? null, this.selectionReason);
        });
    }
    /** Навесить мышь/клавиатуру */
    bind() {
        this.unbind();
        this.abort = new AbortController();
        const { signal } = this.abort;
        // активируем таблицу при клике внутри неё (как раньше: _curtable)
        document.addEventListener('mousedown', (e) => {
            this.active = this.root.contains(e.target);
        }, { signal, capture: true });
        // клики по строкам
        this.root.addEventListener('click', (e) => {
            if (!this.o.isEnabled?.())
                return;
            if (this.o.isCellClickPending?.())
                return;
            const target = e.target;
            if (!(target instanceof Element))
                return;
            if (target.closest(`.${TABLE_CSS_KEY}`) !== this.root)
                return;
            const tr = target.closest(this.o.rowSelector);
            if (!tr || !this.root.contains(tr))
                return;
            if (this.isDisabledRow(tr))
                return;
            const me = e;
            // если это второй клик в последовательности dblclick — игнорируем
            if (me.detail > 1)
                return;
            const id = this.rowId(tr);
            if (!id)
                return;
            // NEW: клик по иконке .r-ic ведём как Ctrl-клик
            const fromIcon = target.classList.contains('r-ic');
            const ctrl = fromIcon || me.ctrlKey || me.metaKey;
            const shift = me.shiftKey;
            const cell = target.closest('td[data-cellclick]');
            const columnKey = cell?.dataset.columnkey;
            // 2026-08-16. При модификаторах приоритет у выбора строк:
            // Ctrl/Shift-клик — это про выделение, а не про выбор ячейки
            const isCellClick = !ctrl && !shift &&
                !!this.o.onCellClick &&
                !!cell &&
                cell.closest(`.${TABLE_CSS_KEY}`) === this.root &&
                cell.closest('tr') === tr &&
                tr.dataset.rowtype !== 'group' &&
                !!columnKey;
            const interactive = target.closest(INTERACTIVE_CELL_SELECTOR);
            if (isCellClick && interactive && cell.contains(interactive))
                return;
            // NEW: убираем текстовое выделение при модификаторах
            if (ctrl || shift) {
                try {
                    window.getSelection?.()?.removeAllRanges();
                }
                catch { }
            }
            this.applySelection(isCellClick ? 'cell' : 'row', () => {
                if (shift) {
                    this.model.range(id, this.order());
                }
                else if (ctrl) {
                    this.model.toggle(id);
                }
                else {
                    this.model.set([id]);
                }
            });
            this.markActiveCell(isCellClick ? cell : null);
            if (isCellClick) {
                this.o.onCellClick?.({
                    rowid: id,
                    columnkey: columnKey
                });
            }
        }, { signal });
        // клавиатура на документ (как раньше)
        document.addEventListener('keydown', (e) => {
            if (!this.active)
                return;
            if (!this.o.isEnabled?.())
                return;
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
                this.handleArrow(e.key === 'ArrowDown', e.shiftKey);
            }
            else if ((e.key === 'a' || e.key === 'A') && (e.ctrlKey || e.metaKey)) {
                // Ctrl/Cmd+A — выделить всё (только в multiple)
                if (this.model.mode === 'multiple') {
                    e.preventDefault();
                    const all = this.order();
                    this.model.set(all);
                }
            }
        }, { signal });
    }
    unbind() {
        this.abort?.abort();
        this.abort = undefined;
    }
    destroy() {
        this.unbind();
    }
    /** Вызвать после перерисовки tbody */
    reconcileAfterReload() {
        const order = this.order();
        this.model.reconcile(order);
        // Если reconcile не изменил ничего — всё уже актуально
        // но на всякий случай подсветим текущую
        this.clearCurrent();
        const focused = this.model.focused;
        if (focused) {
            const tr = this.rowById(focused);
            if (tr)
                tr.classList.add(this.o.currentClass);
        }
    }
    // ── Внутреннее ────────────────────────────────────────────────
    tbody() {
        return this.root.querySelector(this.o.tbodySelector);
    }
    rows() {
        const tbody = this.tbody();
        if (!tbody)
            return [];
        return Array.from(tbody.querySelectorAll(this.o.rowSelector))
            .filter(tr => !this.isDisabledRow(tr));
    }
    isDisabledRow(tr) {
        return tr.matches(this.o.disabledRowSelector);
    }
    rowId(tr) {
        const v = tr.getAttribute(this.o.idAttr);
        return v && v !== '' ? v : null;
    }
    rowById(id) {
        const tbody = this.tbody();
        if (!tbody)
            return null;
        return tbody.querySelector(`${this.o.rowSelector}[${this.o.idAttr}="${id}"]`);
    }
    order() {
        return this.rows()
            .map(tr => this.rowId(tr))
            .filter((x) => !!x);
    }
    clearCurrent() {
        this.root.querySelectorAll(`.${this.o.currentClass}`).forEach(el => el.classList.remove(this.o.currentClass));
    }
    /**
     * 2026-08-16. Помечает активную cell-click ячейку и её строку.
     * Классы живут на td/tr, поэтому перерисовка tbody снимает их сама.
     *
     * @param cell ячейка cell-click либо null, если выбор вернулся к строке
     */
    markActiveCell(cell) {
        this.root.querySelectorAll(`.${CELL_SELECTED_CLASS}`)
            .forEach(el => el.classList.remove(CELL_SELECTED_CLASS));
        this.root.querySelectorAll(`.${CELL_ROW_CLASS}`)
            .forEach(el => el.classList.remove(CELL_ROW_CLASS));
        if (!cell)
            return;
        cell.classList.add(CELL_SELECTED_CLASS);
        cell.closest('tr')?.classList.add(CELL_ROW_CLASS);
    }
    applySelection(reason, action) {
        const previousReason = this.selectionReason;
        this.selectionReason = reason;
        try {
            action();
        }
        finally {
            this.selectionReason = previousReason;
        }
    }
    handleArrow(down, extend) {
        const order = this.order();
        if (order.length === 0)
            return;
        const focused = this.model.focused ?? this.model.selected.values().next().value ?? order[0];
        const idx = Math.max(0, order.indexOf(focused));
        const nextIdx = Math.min(order.length - 1, Math.max(0, idx + (down ? 1 : -1)));
        const nextId = order[nextIdx];
        if (extend) {
            this.model.range(nextId, order);
        }
        else {
            this.model.set([nextId]);
        }
        // Прокрутка «в область видимости» (мягкая)
        const tr = this.rowById(nextId);
        if (tr) {
            // только если не виден — тогда прокручиваем
            const rect = tr.getBoundingClientRect();
            const vpH = window.innerHeight || document.documentElement.clientHeight;
            if (rect.top < 0 || rect.bottom > vpH)
                tr.scrollIntoView({ block: 'nearest' });
        }
    }
    /**
     * Возвращает список всех «выбираемых» id строк таблицы в текущем порядке.
     * Игнорируются строки, попадающие под disabledRowSelector (по умолчанию: 'tr.ar')
     * и заголовки/служебные (rowSelector: 'tr:not(.h)').
     */
    getAllSelectableIds() {
        return this.order();
    }
    /**
     * Выделяет все строки (эквивалент Ctrl/Cmd+A).
     * Работает только когда:
     *  - адаптер «включён» (isEnabled?() !== false),
     *  - модель в режиме multiple.
     * Синхронизирует _ckrow, _currow и hidden-поля.
     */
    selectAll() {
        if (this.o.isEnabled?.() === false)
            return;
        if (this.model.mode !== 'multiple')
            return;
        this.model.set(this.order());
    }
    /**
     * Делает указанную строку «текущей» (focused/_currow) по её id.
     * При append=true добавляет к текущему выбору, иначе заменяет выбор.
     * При scroll=true прокручивает строку в видимую область (nearest).
     *
     * @param id   data-id строки
     * @param opts append — добавлять к выделению; scroll — прокрутить к строке
     * @returns true, если строка найдена и установлена текущей; иначе false
     */
    setCurrentById(id, opts) {
        if (this.o.isEnabled?.() === false)
            return false;
        const tr = this.rowById(id);
        if (!tr || this.isDisabledRow(tr))
            return false;
        if (opts?.append)
            this.model.select(id);
        else
            this.model.set([id]);
        if (opts?.scroll)
            tr.scrollIntoView({ block: 'nearest' });
        return true;
    }
    /**
     * Добавляет визуальные маркеры на указанные строки таблицы.
     * @param ids массив data-id строк, на которые нужно поставить маркер
     * @param clearExisting если true — предварительно снимает маркер со всех строк
     */
    markRows(ids, clearExisting = true) {
        if (!this.o.isEnabled || !Array.isArray(ids) || ids.length === 0)
            return;
        const tbody = this.tbody();
        if (!tbody)
            return;
        if (clearExisting) {
            tbody.querySelectorAll(`.${this.o.selectedClass}`).forEach(el => el.classList.remove(this.o.selectedClass));
        }
        ids.forEach(id => {
            const tr = this.rowById(id);
            if (tr && !this.isDisabledRow(tr)) {
                tr.classList.add(this.o.selectedClass);
            }
        });
    }
}

export { TableSelectionAdapter };
